<template>
    <Swiper
        v-if="slides.length"
        :key="swiperKey"
        v-bind="swiperOptions"
        class="swiper swiper-team mt-5 max-lg:overflow-hidden md:mt-20 lg:!overflow-visible">
        <ArrowButton
            id="our-team-prev"
            aria-label="Предыдущий сотрудник"
            class="absolute left-0 top-1/3 z-10 rotate-180 max-lg:hidden md:left-2" />
        <ArrowButton
            id="our-team-next"
            aria-label="Следующий сотрудник"
            class="absolute right-0 top-1/3 z-10 max-lg:hidden md:right-2" />
        <SwiperSlide
            v-for="slide in slides"
            :key="slide.key"
            class="swiper-slide group pointer-events-auto flex cursor-pointer max-lg:!w-[18rem] lg:!w-[23.75rem] lg:!overflow-visible">
            <div
                class="swiper-slide__inner pointer-events-none select-none max-lg:!w-[18rem] lg:mx-auto lg:w-[23.75rem] lg:opacity-0 lg:blur-md lg:group-[.swiper-slide-visible]:opacity-100 lg:group-[.swiper-slide-fully-visible]:blur-0">
                <div
                    class="relative select-none overflow-hidden rounded-12 max-lg:!size-[18rem] lg:size-[23.75rem]">
                    <picture>
                        <source
                            v-if="slide.webpSrc"
                            type="image/webp"
                            :srcset="slide.webpSrc" />
                        <img
                            loading="lazy"
                            :width="slide.image?.width ?? 380"
                            :height="slide.image?.height ?? 380"
                            :alt="slide.alt"
                            :src="slide.imageSrc ?? ''" />
                    </picture>
                    <div
                        v-if="slide.employee.name"
                        v-html="slide.employee.name"
                        class="absolute bottom-0 left-0 max-w-[81%] rounded-tr-12 bg-accent/80 px-4 py-3 text-20/120 font-semibold text-ash-200" />
                </div>
                <div
                    v-if="slide.employee.position || slide.employee.description"
                    class="mt-3 md:mt-5">
                    <div
                        v-if="slide.employee.position"
                        v-html="slide.employee.position"
                        class="text-20/120 font-semibold text-ash-200" />
                    <div
                        v-if="slide.employee.description"
                        v-html="slide.employee.description"
                        class="mt-2 text-12/140 text-accent-100 md:text-16/150 lg:mt-3" />
                </div>
            </div>
        </SwiperSlide>
    </Swiper>
</template>

<script setup lang="ts">
import { useMediaQuery, useMounted } from '@vueuse/core'
import 'swiper/css'
import 'swiper/css/effect-cards'
import { EffectCards, Keyboard, Navigation } from 'swiper/modules'
import type { KeyboardOptions, SwiperModule, SwiperOptions } from 'swiper/types'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed } from 'vue'
import type { Image } from '~/types/global.types'

import ArrowButton from '~/components/base/arrow-button/ArrowButton.vue'
import type { Employee } from '~/components/particles/our-team/our-team.types'

import { getMediaLink } from '~/utils/getMediaLink'

const props = defineProps<{
    items: Employee[]
}>()

const isMounted = useMounted()
const isDesktopViewport = useMediaQuery('(min-width: 1024px)')
const isDesktop = computed(() => isMounted.value && isDesktopViewport.value)
const swiperKey = computed(() => (isDesktop.value ? 'desktop' : 'mobile'))

const keyboardOptions: KeyboardOptions = {
    enabled: true,
    onlyInViewport: true,
}

interface SlideItem {
    employee: Employee
    image: Image | null
    imageSrc: string | null
    webpSrc: string | null
    alt: string
    key: string
}

const normalizedItems = computed(() => {
    if (!props.items.length) return []
    const base = [...props.items]

    if (!isDesktop.value || base.length >= 15) return base

    return base.concat(base)
})

const slides = computed<SlideItem[]>(() =>
    normalizedItems.value.map((employee, index) => {
        const image = employee.image?.[0] ?? null
        const imageSrc =
            image?.url != null
                ? (getMediaLink(image.url, 600) ?? image.url)
                : null
        const webpSrc =
            image?.webp_url != null
                ? (getMediaLink(image.webp_url, 800) ?? image.webp_url)
                : null
        const alt = image?.alt ?? employee.name ?? employee.position ?? ''

        return {
            employee,
            image,
            imageSrc,
            webpSrc,
            alt,
            key: `${employee.id}-${index}`,
        }
    })
)

const swiperModules = computed<SwiperModule[]>(() =>
    isDesktop.value ? [EffectCards, Navigation, Keyboard] : [Keyboard]
)

const swiperOptions = computed(() => {
    if (!isDesktop.value) {
        return {
            modules: swiperModules.value,
            slidesPerView: 'auto',
            spaceBetween: 8,
            breakpoints: {
                375: { spaceBetween: 20 },
            },
            keyboard: keyboardOptions,
        } satisfies SwiperOptions
    }

    const shouldLoop = slides.value.length > 1

    return {
        modules: swiperModules.value,
        loop: shouldLoop,
        centeredSlides: true,
        slidesPerView: 'auto',
        effect: 'cards',
        simulateTouch: false,
        allowTouchMove: false,
        keyboard: keyboardOptions,
        navigation: {
            prevEl: '#our-team-prev',
            nextEl: '#our-team-next',
        },
        cardsEffect: {
            rotate: false,
            perSlideOffset: 100,
            slideShadows: false,
        },
    } satisfies SwiperOptions
})
</script>

<style scoped>
@media (min-width: 64rem) {
    .swiper-slide {
        perspective: none !important;
        transform-style: flat !important;
    }

    .swiper-slide {
        z-index: 1;
    }

    .swiper-slide.swiper-slide-prev,
    .swiper-slide.swiper-slide-next {
        z-index: 2;
    }

    .swiper-slide.swiper-slide-active {
        z-index: 3;
    }

    .swiper-slide__inner {
        transition-duration: 300ms;
        transition-property: all;
    }

    .swiper-slide:not(.swiper-slide-active) .swiper-slide__inner {
        transform: scale(0.85);
    }

    .swiper-slide:has(+ .swiper-slide.swiper-slide-prev) .swiper-slide__inner {
        transform: scale(0.7) translateX(230px);
    }

    .swiper-slide.swiper-slide-next ~ .swiper-slide .swiper-slide__inner {
        transform: scale(0.7) translateX(-230px);
    }

    .swiper-slide:has(+ .swiper-slide.swiper-slide-prev)
        .swiper-slide__inner:before,
    .swiper-slide.swiper-slide-next
        ~ .swiper-slide
        .swiper-slide__inner:before {
        opacity: 1;
    }
}
</style>
